import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PermissionCode } from '@/graphql/generated/types.generated';
import { FeatureAddonRow } from './FeatureAddonRow';
import { UploadSizeAddOnRow } from './rows/UploadSizeAddOnRow';
import type { FeatureGroup } from './constants';
import type { MappedFeature } from './utils/map-graphql-to-calculator.util';

interface FeatureGroupSectionProps {
    group: FeatureGroup;
    featureByCode: Record<string, MappedFeature>;
    currency?: { symbol: string } | null;
    addOnQty: Record<string, number>;
    addOnBooleans: Record<string, boolean>;
    addOnEnums: Record<string, string>;
    onNumericChange: (key: string, qty: number) => void;
    onBoolChange: (key: string, enabled: boolean) => void;
    onEnumChange: (key: string, value: string) => void;
}

export const FeatureGroupSection = ({
    group,
    featureByCode,
    currency,
    addOnQty,
    addOnBooleans,
    addOnEnums,
    onNumericChange,
    onBoolChange,
    onEnumChange,
}: FeatureGroupSectionProps) => {
    const { t } = useTranslation();
    const features = group.codes.map((k) => featureByCode[k]).filter(Boolean);
    if (features.length === 0) return null;

    return (
        <Box>
            <Typography
                variant="overline"
                fontWeight={700}
                color="text.secondary"
                sx={{ letterSpacing: 1, display: 'block', mb: 1 }}
            >
                {t(group.label)}
            </Typography>
            <Stack spacing={0.5}>
                {features.map((feature) => {
                    // Image + video upload size share one control (they upgrade together).
                    // The video row is folded into the image row and not rendered on its own.
                    if (feature.code === PermissionCode.UploadSizeVideos) return null;

                    if (feature.code === PermissionCode.UploadSizeImages) {
                        const videoFeature = featureByCode[PermissionCode.UploadSizeVideos];
                        if (videoFeature) {
                            return (
                                <UploadSizeAddOnRow
                                    key={feature.code}
                                    imageFeature={feature}
                                    videoFeature={videoFeature}
                                    selectedValue={addOnEnums[feature.code] ?? feature.displayValue}
                                    currency={currency}
                                    onChange={onEnumChange}
                                />
                            );
                        }
                    }

                    return (
                        <FeatureAddonRow
                            key={feature.code}
                            feature={feature}
                            currency={currency}
                            featureByCode={featureByCode}
                            addOnQty={addOnQty}
                            addOnBooleans={addOnBooleans}
                            addOnEnums={addOnEnums}
                            onNumericChange={onNumericChange}
                            onBoolChange={onBoolChange}
                            onEnumChange={onEnumChange}
                        />
                    );
                })}
            </Stack>
        </Box>
    );
};
